// src/pages/public/ContactUs.jsx
import React, { useState } from 'react';
import {
  SendICon,
  EmailIcon,
  PhoneIcon,
  LocationIcon,
  PlusIcon,
  MinusIcon
} from '../../public/icons';
import { FigmaP } from '../../public/images/contact';
import supportService from '../../services/supportService';
import { useToast } from '../../context/ToastContext';

const ContactUs = () => {
  const { success, error: showError } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const faqData = [
    {
      id: 1,
      question: 'How do I attach memories to my products?',
      answer:
        'Every journey is different. Start by choosing what your memoir is about so we can guide you with the right questions.',
      details: [
        'Select the life story you want to document (e.g., love, healing, achievement, loss, becoming)',
        'We tailor the questions to your journey',
        'You can switch categories anytime'
      ]
    },
    {
      id: 2,
      question:
        "What's the difference between Memory Magnets and Legacy Magnets?",
      answer:
        'Memory Magnets and Legacy Magnets serve different purposes in preserving your memories.'
    },
    {
      id: 3,
      question: 'Can I create a memoir without purchasing a product?',
      answer:
        'Yes, you can create and save your memoir digitally without purchasing physical products.'
    },
    {
      id: 4,
      question: 'How does the QR code scanning work?',
      answer:
        'Our QR code system allows easy access to your digital memories through simple scanning.'
    },
    {
      id: 5,
      question: 'What formats do you support for digital memories?',
      answer:
        'We support various formats including images, videos, audio files, and text documents.'
    }
  ];

  /* ────────────── Input handlers ────────────── */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear the field error for this input as the user types
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  /* ────────────── Client-side validation ────────────── */

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Your name is required';
    } else if (formData.name.trim().length > 120) {
      errors.name = 'Name is too long (max 120 characters)';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Your email is required';
    } else if (!emailRegex.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.subject) {
      errors.subject = 'Please select a subject';
    }

    if (!formData.message.trim()) {
      errors.message = 'Your message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Your message must be at least 10 characters';
    } else if (formData.message.trim().length > 5000) {
      errors.message = 'Your message is too long (max 5000 characters)';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ────────────── Submit handler ────────────── */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return;

    if (!validateForm()) {
      showError('Please fix the highlighted fields and try again.');
      return;
    }

    try {
      setSubmitting(true);

      const response = await supportService.submitContact({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        subject: formData.subject,
        message: formData.message.trim()
      });

      if (response?.success) {
        setSubmitSuccess(true);
        success(
          'Message sent! We`ll get back to you shortly.'
        );

        // Reset form
        setFormData({ name: '', email: '', subject: '', message: '' });
        setFieldErrors({});

        // Hide the success banner after a few seconds
        setTimeout(() => setSubmitSuccess(false), 8000);
      } else {
        showError(response?.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Contact submission failed:', err);

      // Extract validation errors if the backend returned them
      const backendErrors = err?.response?.data?.errors;
      if (Array.isArray(backendErrors) && backendErrors.length > 0) {
        const mapped = {};
        backendErrors.forEach((e) => {
          if (e.field) mapped[e.field] = e.message;
        });
        setFieldErrors(mapped);
        showError('Please fix the highlighted fields and try again.');
      } else {
        showError(
          err?.message ||
            'Something went wrong. Please try again in a moment.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* ────────────── FAQ toggle ────────────── */

  const toggleFAQ = (id) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  /* ────────────── Render ────────────── */

  return (
    <div className="min-h-screen bg-[#FCF7EE] text-left">
      {/* Header */}
      <div className="px-4 py-12 md:px-8 lg:px-16">
        <div className="text-left">
          <h1 className="text-4xl md:text-6xl font-bold text-[#1C2A46] mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-[#6B635D] max-w-2xl">
            Do you have any questions? A complaint? or need any help to choose
            the right product from Kapta? Feel free to contact us.
          </p>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* ── Send Us a Message ── */}
          <div>
            <h2 className="text-2xl font-semibold text-[#1C2A46] mb-6">
              Send Us a Message
            </h2>

            {/* Success banner */}
            {submitSuccess && (
              <div
                className="bg-[#E7F4ED] border border-[#2F8F5B] text-[#2F8F5B] px-4 py-3 mb-6 text-sm rounded"
                role="status"
              >
                ✅ Message sent! We'll get back to you within 24–48 hours.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              {/* Name */}
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="enter your name*"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={submitting}
                  className={`w-full px-4 py-3 border bg-transparent focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none placeholder-gray-400 disabled:opacity-60 ${
                    fieldErrors.name
                      ? 'border-[#A82121]'
                      : 'border-[#E4E4E4]'
                  }`}
                  required
                />
                {fieldErrors.name && (
                  <p className="text-xs text-[#A82121] mt-1">
                    {fieldErrors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="enter your email*"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={submitting}
                  className={`w-full px-4 py-3 border bg-transparent focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none placeholder-gray-400 disabled:opacity-60 ${
                    fieldErrors.email
                      ? 'border-[#A82121]'
                      : 'border-[#E4E4E4]'
                  }`}
                  required
                />
                {fieldErrors.email && (
                  <p className="text-xs text-[#A82121] mt-1">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {/* Subject */}
              <div>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  disabled={submitting}
                  className={`w-full px-4 py-3 border bg-transparent focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none placeholder-gray-400 disabled:opacity-60 ${
                    fieldErrors.subject
                      ? 'border-[#A82121]'
                      : 'border-[#E4E4E4]'
                  }`}
                >
                  <option value="">Select a subject</option>
                  <option value="general">General Inquiry</option>
                  <option value="support">Technical Support</option>
                  <option value="billing">Billing Question</option>
                  <option value="feedback">Feedback</option>
                  <option value="complaint">Complaint</option>
                  <option value="other">Other</option>
                </select>
                {fieldErrors.subject && (
                  <p className="text-xs text-[#A82121] mt-1">
                    {fieldErrors.subject}
                  </p>
                )}
              </div>

              {/* Message */}
              <div>
                <textarea
                  name="message"
                  placeholder="Write your message here…*"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  disabled={submitting}
                  maxLength={5000}
                  className={`w-full px-4 py-3 border bg-transparent focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none placeholder-gray-400 disabled:opacity-60 ${
                    fieldErrors.message
                      ? 'border-[#A82121]'
                      : 'border-[#E4E4E4]'
                  }`}
                  required
                />
                <div className="flex justify-between items-center mt-1">
                  {fieldErrors.message ? (
                    <p className="text-xs text-[#A82121]">
                      {fieldErrors.message}
                    </p>
                  ) : (
                    <span />
                  )}
                  <span className="text-xs text-[#8A9099]">
                    {formData.message.length}/5000
                  </span>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-5 px-6 transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Sending…
                  </>
                ) : (
                  <>
                    <SendICon className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>

              <p className="text-xs text-[#8A9099] text-center">
                We usually respond within 24–48 hours. Check your email inbox
                (and spam folder) for our reply.
              </p>
            </form>
          </div>

          {/* ── Get in Touch ── */}
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-[#1C2A46]">
              Get in Touch
            </h2>

            <div className="space-y-6">
              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="bg-[#55000C] h-[74px] w-[74px] flex justify-center items-center rounded-full flex-shrink-0">
                  <EmailIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#1C2A46] mb-1">
                    Email Us
                  </h3>
                  <a
                    href="mailto:hello@kapta.com"
                    className="block text-gray-600 hover:text-[#55000C]"
                  >
                    hello@kapta.com
                  </a>
                  <a
                    href="mailto:support@kapta.com"
                    className="block text-gray-600 hover:text-[#55000C]"
                  >
                    support@kapta.com
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="bg-[#55000C] h-[74px] w-[74px] flex justify-center items-center rounded-full flex-shrink-0">
                  <PhoneIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#1C2A46] mb-1">
                    Call Us
                  </h3>
                  <a
                    href="tel:+15551234567"
                    className="block text-gray-600 hover:text-[#55000C]"
                  >
                    +1 (555) 123-4567
                  </a>
                  <p className="text-sm text-gray-500">
                    Mon-Fri, 9am-6pm EST
                  </p>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="bg-[#55000C] h-[74px] w-[74px] flex justify-center items-center rounded-full flex-shrink-0">
                  <LocationIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-[#1C2A46] mb-1">
                    Address
                  </h3>
                  <p className="text-gray-600">123 Memory Lane</p>
                  <p className="text-gray-600">San Francisco, CA 94102</p>
                  <p className="text-gray-600">United States</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-12">
          <img src={FigmaP} alt="Kapta office location" />
        </div>

        {/* FAQ */}
        <div className="mt-16 pb-16">
          <h2 className="text-3xl font-medium text-[#1C2A46] mb-16 border-b-2 pb-5 border-[#E4E4E4]">
            FAQ (Frequently Asked Questions)
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {faqData.map((faq) => (
              <div
                key={faq.id}
                className="border-b-2 pb-5 border-[#E4E4E4]"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full p-1 text-left flex items-center justify-between transition-colors duration-200"
                >
                  <h3 className="text-lg font-medium text-[#1C2A46] pr-4">
                    {faq.question}
                  </h3>
                  {expandedFAQ === faq.id ? (
                    <MinusIcon className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  ) : (
                    <PlusIcon className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  )}
                </button>

                {expandedFAQ === faq.id && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 mb-4">{faq.answer}</p>
                    {faq.details && (
                      <ul className="space-y-2">
                        {faq.details.map((detail, index) => (
                          <li
                            key={index}
                            className="text-gray-600 text-sm flex items-start gap-2"
                          >
                            <span className="text-yellow-600 mt-1.5">•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;